/**
 * Skeleton - Loading placeholder components
 *
 * Provides visual loading states for content that's being fetched.
 * Uses Tailwind animate-pulse for the pulse effect.
 */

import React from 'react';
import { twMerge } from 'tailwind-merge';
import { Card } from './ui/Card';

interface SkeletonProps {
  /** Width of the skeleton (CSS value or number for pixels) */
  width?: string | number;
  /** Height of the skeleton (CSS value or number for pixels) */
  height?: string | number;
  /** Shape variant */
  variant?: 'text' | 'circular' | 'rectangular' | 'rounded';
  /** Additional CSS classes */
  className?: string;
  /** Animation type */
  animation?: 'pulse' | 'wave' | 'none';
}

const variantClasses: Record<NonNullable<SkeletonProps['variant']>, string> = {
  text: 'h-4 rounded',
  circular: 'rounded-full',
  rectangular: 'rounded-none',
  rounded: 'rounded-lg',
};

const animationClasses: Record<NonNullable<SkeletonProps['animation']>, string> = {
  pulse: 'bg-bg-secondary animate-pulse motion-reduce:animate-none',
  // Shimmer sweep: a moving gradient stands in for the background color, so
  // this variant does NOT include bg-bg-secondary at the base (see below).
  // Under prefers-reduced-motion: motion-reduce:bg-none cancels the gradient
  // background-image (background-image always paints over background-color,
  // so the color alone wouldn't be visible without this), and
  // motion-reduce:bg-bg-secondary then supplies the same flat fill pulse/none
  // use, so reduced-motion users get a plain static skeleton block instead of
  // either the frozen gradient or nothing.
  wave: 'bg-[linear-gradient(100deg,var(--theme-skeleton-base)_35%,var(--theme-skeleton-shine)_50%,var(--theme-skeleton-base)_65%)] bg-[length:250%_100%] animate-shimmer motion-reduce:animate-none motion-reduce:bg-none motion-reduce:bg-bg-secondary',
  none: 'bg-bg-secondary',
};

export const Skeleton: React.FC<SkeletonProps> = ({
  width,
  height,
  variant = 'text',
  className = '',
  animation = 'pulse',
}) => {
  const style: React.CSSProperties = {
    width: typeof width === 'number' ? `${width}px` : width,
    height: typeof height === 'number' ? `${height}px` : height,
  };

  return (
    <div
      className={twMerge(
        'block',
        variantClasses[variant],
        animationClasses[animation],
        className,
      )}
      style={style}
      aria-hidden="true"
    />
  );
};

interface SkeletonTextProps {
  /** Number of lines to render */
  lines?: number;
  /** Width of the last line (percentage) */
  lastLineWidth?: string;
  /** Gap between lines */
  gap?: string | number;
  /** Additional CSS classes */
  className?: string;
  /**
   * Animation type. Defaults to 'pulse' -- a standalone paragraph placeholder
   * reads as calm body copy loading in, and 'wave' feels like a progress-bar
   * gimmick for that case (design audit, docs/reference/bug-registry.md's
   * companion Adoption Ledger). Pass 'wave' explicitly only where this is
   * repeated as list/timeline rows rather than a single paragraph.
   */
  animation?: SkeletonProps['animation'];
}

export const SkeletonText: React.FC<SkeletonTextProps> = ({
  lines = 3,
  lastLineWidth = '60%',
  gap = 8,
  className = '',
  animation = 'pulse',
}) => {
  return (
    <div
      className={twMerge('flex flex-col', className)}
      style={{ gap: typeof gap === 'number' ? `${gap}px` : gap }}
      aria-hidden="true"
    >
      {Array.from({ length: lines }).map((_, index) => (
        <Skeleton
          key={index}
          variant="text"
          width={index === lines - 1 ? lastLineWidth : '100%'}
          animation={animation}
        />
      ))}
    </div>
  );
};

interface SkeletonCardProps {
  /** Show avatar placeholder */
  hasAvatar?: boolean;
  /** Number of text lines */
  lines?: number;
  /** Show action button placeholder */
  hasAction?: boolean;
  /** Additional CSS classes */
  className?: string;
  /**
   * Animation type. Defaults to 'wave' -- SkeletonCard is overwhelmingly used
   * for repeated grid/list cards (design audit, docs/reference/bug-registry.md's
   * companion Adoption Ledger), where a shimmer sweep reads as "content
   * streaming in." Pass 'pulse' explicitly for the rarer case of a single
   * full-page card loading state, where a lone shimmering block reads busier
   * than calm.
   */
  animation?: SkeletonProps['animation'];
}

export const SkeletonCard: React.FC<SkeletonCardProps> = ({
  hasAvatar = false,
  lines = 2,
  hasAction = false,
  className = '',
  animation = 'wave',
}) => {
  return (
    <Card padding="none" className={twMerge('p-5 shadow-sm', className)} aria-hidden="true">
      {hasAvatar && (
        <div className="flex items-center gap-3 mb-4">
          <Skeleton variant="circular" width={40} height={40} animation={animation} />
          <div className="flex-1 flex flex-col gap-1.5">
            <Skeleton variant="text" width="60%" height={16} animation={animation} />
            <Skeleton variant="text" width="40%" height={12} animation={animation} />
          </div>
        </div>
      )}
      <div className="mb-4">
        <SkeletonText lines={lines} animation={animation} />
      </div>
      {hasAction && (
        <div className="flex justify-end">
          <Skeleton variant="rounded" width={80} height={36} animation={animation} />
        </div>
      )}
    </Card>
  );
};

interface SkeletonTableProps {
  /** Number of rows */
  rows?: number;
  /** Number of columns */
  columns?: number;
  /** Show header row */
  hasHeader?: boolean;
  /** Additional CSS classes */
  className?: string;
}

export const SkeletonTable: React.FC<SkeletonTableProps> = ({
  rows = 5,
  columns = 4,
  hasHeader = true,
  className = '',
}) => {
  return (
    <div className={twMerge('w-full', className)} aria-hidden="true">
      {hasHeader && (
        <div
          className="grid gap-4 px-4 py-3 border-b border-border-light"
          style={{ gridTemplateColumns: `repeat(${columns}, minmax(80px, 1fr))` }}
        >
          {Array.from({ length: columns }).map((_, index) => (
            <Skeleton key={index} variant="text" height={14} />
          ))}
        </div>
      )}
      <div className="flex flex-col">
        {Array.from({ length: rows }).map((_, rowIndex) => (
          <div
            key={rowIndex}
            className="grid gap-4 p-4 border-b border-border-light last:border-b-0"
            style={{ gridTemplateColumns: `repeat(${columns}, minmax(80px, 1fr))` }}
          >
            {Array.from({ length: columns }).map((_, colIndex) => (
              <Skeleton key={colIndex} variant="text" height={16} />
            ))}
          </div>
        ))}
      </div>
    </div>
  );
};

interface SkeletonListProps {
  /** Number of items */
  count?: number;
  /** Show avatar for each item */
  hasAvatar?: boolean;
  /** Show secondary text line */
  hasSecondary?: boolean;
  /** Additional CSS classes */
  className?: string;
  /**
   * Animation type. Defaults to 'wave' -- every SkeletonList usage found in a
   * full-app audit was a repeated row (avatar + lines), the textbook shimmer
   * case (design audit, docs/reference/bug-registry.md's companion Adoption
   * Ledger). No contradicting context found, so unlike SkeletonCard/Text this
   * has no documented reason to override to 'pulse' -- the prop still exists
   * for a future exception, not because one is known today.
   */
  animation?: SkeletonProps['animation'];
}

export const SkeletonList: React.FC<SkeletonListProps> = ({
  count = 5,
  hasAvatar = true,
  hasSecondary = true,
  className = '',
  animation = 'wave',
}) => {
  return (
    <div className={twMerge('flex flex-col', className)} aria-hidden="true">
      {Array.from({ length: count }).map((_, index) => (
        <div key={index} className="flex items-center gap-3 py-3 border-b border-border-light last:border-b-0">
          {hasAvatar && <Skeleton variant="circular" width={40} height={40} animation={animation} />}
          <div className="flex-1 flex flex-col gap-1.5">
            <Skeleton variant="text" width="70%" height={16} animation={animation} />
            {hasSecondary && <Skeleton variant="text" width="50%" height={12} animation={animation} />}
          </div>
        </div>
      ))}
    </div>
  );
};
